import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';
import { Button, Divider } from 'antd';
import { TbArrowUp } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { MiddleDot } from '@/shared/components/typography';

interface ScrollEndFooterProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
    actionLabel?: ReactNode;
    label: ReactNode;
    onBackToTop: () => void;
    paddingBlock?: CSSProperties['paddingBlock'];
}

/** Shared end-of-scroll divider with semantic small text and a smooth-scroll action. */
const ScrollEndFooter = forwardRef<HTMLDivElement, ScrollEndFooterProps>(
    ({ actionLabel = 'Back to Top', label, onBackToTop, paddingBlock, style, ...props }, ref) => {
        const { token } = useAntDesignTheme();

        return (
            <div
                {...props}
                data-scroll-end-footer='true'
                ref={ref}
                style={{ paddingBlock: paddingBlock ?? token.paddingSM, ...style }}
            >
                <Divider plain style={{ margin: 0 }}>
                    <span
                        style={{
                            alignItems: 'center',
                            color: token.colorTextTertiary,
                            display: 'inline-flex',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        <small>{label}</small>
                        <MiddleDot horizontalMargin='0.375rem' />
                        <Button
                            onClick={onBackToTop}
                            size='small'
                            style={{ height: 'auto', lineHeight: 'inherit' }}
                            type='link'
                        >
                            <small
                                style={{
                                    alignItems: 'center',
                                    display: 'inline-flex',
                                    gap: token.paddingXXS,
                                }}
                            >
                                <TbArrowUp aria-hidden size='1em' />
                                {actionLabel}
                            </small>
                        </Button>
                    </span>
                </Divider>
            </div>
        );
    },
);

ScrollEndFooter.displayName = 'ScrollEndFooter';

export default ScrollEndFooter;
export type { ScrollEndFooterProps };
